import { FC, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import { customFieldOptions } from '@features/admin/reports/reportData';
import axios from 'axios';
import { toast } from 'react-toastify';
import { TReportUserDistributionByCustomFieldData } from '@src/types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportTableColumns } from '@hooks/useReportTableColumns';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: TReportUserDistributionByCustomFieldData;
  }>;
}

interface UserDistributionResponse {
  field_name: string;
  distributions: TReportUserDistributionByCustomFieldData[];
  total_users: number;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col items-center min-w-[135px] gap-y-1"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#B6D72C] text-xl font-semibold">
          {payload[0]?.value.toLocaleString()}
        </p>
        <p className="text-[#999999] text-[10px]">
          {payload[0]?.payload?.field_value}
        </p>
        <p className="text-white text-xs">
          Users: {payload[0]?.value.toLocaleString()}
        </p>
      </div>
    );
  }

  return null;
};

export const UserDistributionByCustomField: FC = () => {
  // Data state
  const [data, setData] = useState<TReportUserDistributionByCustomFieldData[]>(
    []
  );
  const [, setTotalUsers] = useState<number>(0);

  // Filter state
  const [selectedField, setSelectedField] = useState<string>(
    customFieldOptions[0]?.value || ''
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Use custom hook for table columns
  const { columns, tableData } =
    useReportTableColumns<TReportUserDistributionByCustomFieldData>({
      data,
      getAccessField: (item: TReportUserDistributionByCustomFieldData) =>
        item.field_value,
      getMainDataField: (item: TReportUserDistributionByCustomFieldData) =>
        item.user_count || 0,
      includeTotalsRow: true,
      customColumns: [
        {
          title: 'USER COUNT',
          className: 'text-right',
          render: (item) => (
            <p className="text-white">
              {item?.user_count?.toLocaleString() ?? 0}
            </p>
          ),
        },
      ],
    });

  const handleRunReport = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get<UserDistributionResponse>(
        '/reports/user-distribution-by-custom-field/'
      );

      if (response.data) {
        setData(response.data.distributions);
        setTotalUsers(response.data.total_users);
      }
    } catch (error) {
      toast.error('Error fetching report');
      console.error('Error fetching report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        '/reports/user-distribution-by-custom-field/export/',
        {
          responseType: 'blob',
        }
      );

      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute(
        'download',
        'user_distribution_by_custom_field_report.xlsx'
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Report downloaded successfully');
    } catch (error) {
      toast.error('Error downloading report');
      console.error('Error downloading report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        User Distribution By Custom Field
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          fieldOptions={customFieldOptions}
          isLoading={isLoading}
          selectedField={selectedField}
          showFieldFilter={true}
          showPeriodFilter={false}
          onFieldChange={setSelectedField}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <BarChart data={data}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="field_value"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'transparent' }}
              />
              <Legend iconType="line" wrapperStyle={{ fontSize: '14px' }} />
              <Bar dataKey="user_count" fill="#B6D72C" name="User Count" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex justify-end">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              disabled={isLoading}
              variant="outline"
              onClick={handleDownloadCSV}
            >
              {isLoading ? 'Downloading...' : 'Download Excel'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
